import {
  SIGNUP_REFUSED_MESSAGE,
  SIGNUP_REFUSED_TITLE,
  isSignupRefusalDescription,
} from './signup-refusal';

export const PENDING_EMAIL_CONFIRMATION_KEY = 'mora.pending-email-confirmation';
export const PENDING_EMAIL_LINK_ERROR_KEY = 'mora.pending-email-link-error';

const PENDING_CONFIRMATION_TTL_MS = 24 * 60 * 60 * 1_000;

type ConfirmationStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface PendingEmailConfirmation {
  email: string;
  createdAt: number;
  lastSentAt: number;
  returnTo: string;
}

export interface MailboxShortcut {
  label: 'Open Gmail' | 'Open Outlook';
  url: string;
}

const GMAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com']);
const OUTLOOK_DOMAINS = new Set(['outlook.com', 'hotmail.com', 'live.com', 'msn.com']);

export function isAppleAuthVisible(
  providerFlag: string | undefined,
  uiReadinessFlag: string | undefined,
): boolean {
  return providerFlag === 'true' && uiReadinessFlag === 'true';
}

export function confirmationRedirectUrl(origin: string, returnTo: string): string {
  const callback = new URL('/auth', origin);
  callback.searchParams.set('confirmation', '1');
  const setDestination = (destination: string): string => {
    callback.searchParams.set('return_to', destination);
    return callback.toString();
  };
  if (returnTo.includes('\\')) return setDestination('/onboarding');
  try {
    const resolved = new URL(returnTo === '/' ? '/onboarding' : returnTo, origin);
    if (resolved.origin !== new URL(origin).origin) return setDestination('/onboarding');
    return setDestination(resolved.pathname + resolved.search + resolved.hash);
  } catch {
    return setDestination('/onboarding');
  }
}

export interface EmailLinkError {
  title: string;
  description: string;
  kind?: 'signup_refused';
}

export function emailLinkErrorFromLocation(
  hash: string,
  search: string,
  storage?: ConfirmationStorage,
): EmailLinkError | null {
  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
  const searchParams = new URLSearchParams(search.replace(/^\?/, ''));
  const code = hashParams.get('error_code') ?? searchParams.get('error_code');
  const error = hashParams.get('error') ?? searchParams.get('error');
  if (!code && !error) {
    if (!storage) return null;
    try {
      const stored = storage.getItem(PENDING_EMAIL_LINK_ERROR_KEY);
      if (!stored) return null;
      storage.removeItem(PENDING_EMAIL_LINK_ERROR_KEY);
      const parsed: unknown = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof (parsed as EmailLinkError).title === 'string' &&
        typeof (parsed as EmailLinkError).description === 'string'
      ) {
        return parsed as EmailLinkError;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (storage) {
    try {
      storage.removeItem(PENDING_EMAIL_LINK_ERROR_KEY);
    } catch {
      // The current URL still carries the error when storage is unavailable.
    }
  }

  const description = hashParams.get('error_description') ?? searchParams.get('error_description');
  if (isSignupRefusalDescription(description)) {
    return {
      title: SIGNUP_REFUSED_TITLE,
      description: SIGNUP_REFUSED_MESSAGE,
      kind: 'signup_refused',
    };
  }

  if (code === 'otp_expired') {
    return { title: 'That link expired', description: 'Request a new link to continue.' };
  }
  return {
    title: 'That link no longer works',
    description: 'Request a new link or sign in to continue.',
  };
}

export function captureEmailLinkError(
  storage: ConfirmationStorage,
  hash: string,
  search: string,
): void {
  const error = emailLinkErrorFromLocation(hash, search);
  if (!error) return;
  try {
    storage.setItem(PENDING_EMAIL_LINK_ERROR_KEY, JSON.stringify(error));
  } catch {
    // The live URL remains available when storage is blocked.
  }
}

export function mailboxShortcutFor(email: string): MailboxShortcut | null {
  const parts = email.trim().toLowerCase().split('@');
  const domain = parts[parts.length - 1];
  if (!domain) return null;

  if (GMAIL_DOMAINS.has(domain)) {
    return { label: 'Open Gmail', url: 'https://mail.google.com/' };
  }

  if (OUTLOOK_DOMAINS.has(domain)) {
    return { label: 'Open Outlook', url: 'https://outlook.live.com/mail/' };
  }

  return null;
}

function isPendingEmailConfirmation(value: unknown): value is PendingEmailConfirmation {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PendingEmailConfirmation>;
  return (
    typeof candidate.email === 'string' &&
    candidate.email.includes('@') &&
    typeof candidate.createdAt === 'number' &&
    Number.isFinite(candidate.createdAt) &&
    typeof candidate.lastSentAt === 'number' &&
    Number.isFinite(candidate.lastSentAt) &&
    typeof candidate.returnTo === 'string' &&
    candidate.returnTo.startsWith('/') &&
    !candidate.returnTo.startsWith('//') &&
    !candidate.returnTo.includes('\\')
  );
}

export function readPendingEmailConfirmation(
  storage: ConfirmationStorage,
  now = Date.now(),
): PendingEmailConfirmation | null {
  let stored: string | null;
  try {
    stored = storage.getItem(PENDING_EMAIL_CONFIRMATION_KEY);
  } catch {
    return null;
  }
  if (!stored) return null;

  try {
    const value: unknown = JSON.parse(stored);
    if (
      !isPendingEmailConfirmation(value) ||
      now - value.createdAt > PENDING_CONFIRMATION_TTL_MS ||
      value.createdAt > now
    ) {
      try {
        storage.removeItem(PENDING_EMAIL_CONFIRMATION_KEY);
      } catch {
        // Storage can be blocked in hardened/private browser modes.
      }
      return null;
    }
    return value;
  } catch {
    try {
      storage.removeItem(PENDING_EMAIL_CONFIRMATION_KEY);
    } catch {
      // Storage can be blocked in hardened/private browser modes.
    }
    return null;
  }
}

export function writePendingEmailConfirmation(
  storage: ConfirmationStorage,
  value: PendingEmailConfirmation,
): void {
  try {
    storage.setItem(PENDING_EMAIL_CONFIRMATION_KEY, JSON.stringify(value));
  } catch {
    // The live in-memory UI state still works when storage is unavailable.
  }
}

export function clearPendingEmailConfirmation(storage: ConfirmationStorage): void {
  try {
    storage.removeItem(PENDING_EMAIL_CONFIRMATION_KEY);
  } catch {
    // Clearing unavailable storage is a no-op.
  }
}
